import * as React from 'react';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import {colors, registerGlobalStyle} from '../../../theme';
import {Fields} from '../../Layout';
import {asCurrencyStringCommaSeparated, formatDateShort} from '../../../utils';
import {CellContainer} from './CellContainer';
import {useCallback, useState} from 'react';
import {Chip} from '../../Chips';
import {awaitTimeout} from '../../../utils/awaitTimeout';
import {useSyncInvoice} from '../../../hooks/api';
import {Button, ToolTip} from '../../Basic';
import {nowAsInternationalDate, utcMidnightAsInternationalDate} from '../../../utils/internationalDate';

export const ACCOUNTING_STATUS = {
  CREATING: {
    color: colors.palette.orange,
    label: 'Sending to Accounts',
    message: 'Your invoice data is being sent to your Accounting software.',
  },
  OVERDUE: {
    color: colors.palette.red,
    label: 'Overdue'
  },
  ERROR: {
    color: colors.palette.red,
    label: 'Error Sending to Accounts'
  },
  PAID_IN_FULL: {
    color: colors.palette.green,
    label: 'Paid'
  },
  PARTIALLY_PAID: {
    color: colors.palette.purple,
    label: 'Partially Paid'
  },
  CURRENT: {
    color: colors.palette.blue,
    label: 'Waiting Payment'
  }
};

registerGlobalStyle('.accounting-status-cell-tooltip', (theme) => ({
  '.button': {
    color: theme.colors.text.contrast,
    fontSize: 'inherit',
    fontWeight: theme.fontWeight.normal,
    margin: '4px 0 0 -2px',
    display: 'flex',
    textDecoration: 'none',
    div: {textDecoration: 'underline'}
  },
  'span': {fontWeight: theme.fontWeight.normal},
}));

export function AccountingStatusCell({invoice, colDef, id, row}) {
  const [syncing, setSyncing] = useState(false);
  const {sync: syncApi} = useSyncInvoice();
  const isClassicJob = !!row.digest;
  const updateField = colDef.context.updateField;
  const refetch = colDef.context.refetch;

  let status = invoice?.status;
  if (status === 'CURRENT' && invoice?.dueDate) {
    const due = utcMidnightAsInternationalDate(invoice.dueDate);
    const now = nowAsInternationalDate();
    if (due <= now) {
      status = 'OVERDUE';
    }
  }
  const {color, label} = ACCOUNTING_STATUS[status] ?? ACCOUNTING_STATUS.ERROR;

  const handleResync = useCallback(async () => {
    setSyncing(true);
    try {
      const invoiceRes = await syncApi({id: invoice._id});
      if (invoiceRes.status) {
        updateField({
          id,
          props: isClassicJob ? {'digest.invoice': invoiceRes.status} : {
            'summary.invoice': invoiceRes.status,
            invoice: invoiceRes.invoice,
          },
          reload: false,
        });
      }
      await awaitTimeout({seconds: 10});
      await refetch();
    } finally {
      setSyncing(false);
    }
  }, [refetch, syncApi, invoice?._id, updateField, id, isClassicJob]);

  return ((invoice ?? null) &&
    <CellContainer>
      <ToolTip className={'accounting-status-cell-tooltip'} tip={
        <>
          {!invoice.errorMessage &&
            <Fields leftLabel noRowGap>
              <span>Invoice Number</span>
              <div>{invoice.invoiceNumber ?? '-'}</div>
              <span>Due Date</span>
              <div>{formatDateShort(invoice.dueDate)}</div>
              <span>Total Amount</span>
              <div className={'currency-prefix'}>{asCurrencyStringCommaSeparated(invoice.totalAmount)}</div>
              <span>Amount Owing</span>
              <div className={'currency-prefix'}>{asCurrencyStringCommaSeparated(invoice.balance)}</div>
            </Fields>
          }
          {invoice.errorMessage &&
            <div>
              An error occurred syncing the invoice.
              {invoice.errorMessage &&
                <>
                  <hr />
                  <div>{invoice.errorMessage}</div>
                </>
              }
            </div>
          }
          <Button actionInline prefix={syncing ? null : 'autorenew'} text={'Resync now'} onClick={handleResync} loading={syncing} disabled={syncing}/>
        </>
      }>
        {!syncing &&
          <Chip text={label} color={color} icon={invoice.errorMessage ? ErrorIcon : InfoIcon}/>
        }
        {syncing &&
          <Chip text={'Syncing'} color={invoice.errorMessage ? colors.palette.red : colors.palette.blue} loading={true}/>
        }
      </ToolTip>
    </CellContainer>
  );
}
